import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';

/**
 * THE DESK EXIT — the only way a human observation of a departure enters the
 * system (POST /api/visitor-log/:id/exit, migration 059).
 *
 * Uses the DEFAULT axios instance on purpose, so the call inherits the
 * interceptors from axios-setup.ts: withCredentials for the httpOnly session
 * cookies, the X-CSRF-Token double-submit header, and the single-flight
 * 401 -> refresh -> retry.
 *
 * IT TARGETS A ROW, NEVER A NAME. Visitor names are AES-GCM with a random IV
 * since migration 040, so no SQL predicate can match one — "sign out Maria" has
 * to become "sign out visitor_log 8817" HERE, after the caller has matched a
 * name against the building's open arrivals. There is deliberately no
 * name-taking variant of this function to reach for.
 *
 * The server supplies exit_source='desk' and departed_by from the session. The
 * body is empty because there is nothing in this request a caller may steer.
 */

export type VisitorExitConflict = 'ALREADY_DEPARTED' | 'EXIT_BEFORE_ARRIVAL';

export class VisitorExitError extends Error {
  /** null when the request never reached the server (offline, DNS, CORS). */
  readonly status: number | null;

  /**
   * Set only on a 409 the server named. Every other failure leaves this null,
   * so `if (error.code)` reads as "this is a rule the desk can act on" rather
   * than a transport or permission failure.
   */
  readonly code: VisitorExitConflict | null;

  /** On ALREADY_DEPARTED: the departure that already exists. */
  readonly departedAt: string | null;

  /**
   * 'desk' (a human recorded it) or 'auto' (the nightly sweep closed it). The
   * difference is the whole point of the column: a cleanup must never be
   * reported back to the desk as somebody's observation.
   */
  readonly exitSource: string | null;

  constructor(
    message: string,
    status: number | null,
    code: VisitorExitConflict | null,
    departedAt: string | null,
    exitSource: string | null
  ) {
    super(message);
    this.name = 'VisitorExitError';
    this.status = status;
    this.code = code;
    this.departedAt = departedAt;
    this.exitSource = exitSource;
  }
}

interface ExitErrorBody {
  message?: string;
  code?: VisitorExitConflict;
  departed_at?: string;
  exit_source?: string;
}

export const signVisitorOut = async (visitorLogId: number): Promise<void> => {
  try {
    await axios.post(`${apiRoute}/visitor-log/${visitorLogId}/exit`);
  } catch (e) {
    const err = e as AxiosError<ExitErrorBody>;
    const body = err.response?.data;
    const status = err.response?.status ?? null;

    throw new VisitorExitError(
      body?.message ||
        (status === 404
          ? 'That arrival is not on this building any more. Reload the list.'
          : 'Could not record the departure.'),
      status,
      body?.code ?? null,
      body?.departed_at ?? null,
      body?.exit_source ?? null
    );
  }
};

/**
 * THE RESIDENT IN/OUT RECORD — 'Resident Entry'.
 *
 * A deliberately minimal body. Every field the visitor wizard collects is a
 * question about an OUTSIDER — was this person approved, is their ID checked,
 * did they get a parking validation — and none of them has an answer when the
 * person walking past the desk lives here. So this posts the four facts that
 * make the row true and nothing else.
 *
 * `unit_id` is what makes the entry addressable: the server resolves the host
 * occupancy from it, which is how the row reaches the resident's own portal
 * (lb_my_visitors, migration 074). It is taken from the ROSTER row rather than
 * typed, so it cannot disagree with the person.
 *
 * The kind string is 'Resident Entry', not 'Resident': the latter is already a
 * daily-log activity ("a note about a resident"), and Lobbero IQ's ENTRY_KINDS
 * is the union of both vocabularies — a duplicate member would make every plan
 * the model writes ambiguous. Kept in step with
 * lobbero_be/src/iq/vocabulary.js.
 */
export const RESIDENT_ENTRY_KIND = 'Resident Entry';

export interface ResidentEntryInput {
  property_ids: number[];
  unit_id: number;
  first_name: string;
  last_name: string;
}

export const logResidentEntry = async (
  input: ResidentEntryInput
): Promise<void> => {
  await axios.post(`${apiRoute}/visitor-log`, {
    visitorType: RESIDENT_ENTRY_KIND,
    visitorDetails: '',
    // Index-aligned arrays, one entry per person, exactly as the wizard sends
    // them — the server encrypts each element and the provenance keys depend on
    // the alignment.
    names: [input.first_name],
    middleNames: [''],
    lastNames: [input.last_name],
    numberOfGuests: 1,
    // The host IS the arrival. Sending the name lets the server's capture step
    // resolve the occupancy inside the unit below, which is what links the row
    // to the resident's own portal feed.
    host_name: `${input.first_name} ${input.last_name}`.trim(),
    unit_id: input.unit_id,
    property_ids: input.property_ids,
    // Not asked, and deliberately blank rather than defaulted to 'Yes': a
    // resident is not approved at the door, so an approval value here would be
    // a fact nobody established.
    approval: '',
    parkingValidation: '',
    preAuthorized: '',
    notes: '',
    description: '',
    details: '',
  });
};
